import type { Document } from "@contentful/rich-text-types";

import type { IVariantSchematic } from "../../../@types/generated/contentful";
import type { ImageData } from "../components/util";

import type { KeyValue } from "./toKeyValue";

export interface VariantSchematic {
  id: string;
  matchOptions: KeyValue[] | null;
  content: Document | null;
  specs: KeyValue[] | null;
  images: ImageData[] | null;
}

export function formatSchematics(
  schematics: IVariantSchematic[]
): VariantSchematic[] {
  return schematics.map((schematic) => {
    const {
      sys: { id },
      fields: {
        name,
        images: rawImages,
        content = null,
        specs: rawSpecs,
        matchOptions: rawMatchOptions,
      },
    } = schematic;

    const isValidSpec = (data: unknown): boolean => {
      if (!data || typeof data !== "object") return false;

      const dataAsRecord = data as Record<string, unknown>;

      if (!("key" in dataAsRecord) || !("value" in dataAsRecord)) return false;
      if (
        typeof dataAsRecord.key !== "string" ||
        typeof dataAsRecord.value !== "string"
      )
        return false;

      return true;
    };

    if (
      rawSpecs &&
      (!Array.isArray(rawSpecs) || rawSpecs.some((spec) => !isValidSpec(spec)))
    )
      throw new Error(
        `Variant schematic specs should be an array, issue with "${name}"`
      );

    if (
      rawMatchOptions &&
      (!Array.isArray(rawMatchOptions) ||
        rawMatchOptions.some((option) => !isValidSpec(option)))
    )
      throw new Error(
        `Variant schematic matchOptions should be an array, issue with "${name}"`
      );

    const specs = rawSpecs ? (rawSpecs as KeyValue[]) : null;
    const matchOptions = rawMatchOptions
      ? (rawMatchOptions as KeyValue[])
      : null;

    if (!specs && !content)
      throw new Error(
        `Variant schematic must have specs or rich text content, issue with "${name}"`
      );

    const formattedImages = rawImages?.map(
      ({
        fields: {
          title,
          description,
          file: {
            url: src,
            details: { image },
          },
        },
      }) => ({
        src,
        alt: description || title,
        height: image?.height,
        width: image?.width,
      })
    );

    return {
      id,
      matchOptions,
      content,
      specs,
      images: formattedImages ?? null,
    };
  });
}

/**
 * Finds the first schematic that matches the selected options.
 *
 * @param schematics - An array of schematics, each containing match options.
 * @param selectedOptions - An object representing the selected options with keys as option names and values as selected values.
 */
export function findFirstMatchingSchematic(
  schematics: VariantSchematic[],
  selectedOptions: Record<string, string>
) {
  return schematics.find((schematic) => {
    const isMatch =
      !schematic.matchOptions ||
      schematic.matchOptions.every((option) => {
        const selectedValue = selectedOptions[option.key];
        if (selectedValue) {
          const values = option.value.split(/,\s*/).map((v) => v.trim());
          return values.includes(selectedValue);
        }
        return false;
      });

    return isMatch;
  });
}
